import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import tierService, { Tier, TierLimitCheck } from '../services/tierService';
import { toast } from 'react-hot-toast';

export const useUserTier = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use tier information from user profile instead of separate API call
  const currentTier = user?.tier ? {
    id: user.tier.id,
    name: user.tier.name,
    display_name: user.tier.display_name,
    price_monthly: user.tier.price_monthly,
    price_yearly: user.tier.price_yearly
  } : null;
  
  const tierLoading = false; // No loading since we get it from user context

  // Check if user can access a feature
  const checkFeatureAccess = async (featureKey: string): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      const limitCheck = await tierService.checkUserLimit(user.id, featureKey);
      return limitCheck.allowed;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  };

  // Track feature usage
  const trackFeatureUsage = useMutation({
    mutationFn: async ({ featureKey, amount = 1 }: { featureKey: string; amount?: number }) => {
      if (!user?.id) throw new Error('User not authenticated');
      return tierService.incrementUserUsage(user.id, featureKey, amount);
    },
    onError: (error) => {
      console.error('Error tracking feature usage:', error);
      toast.error('Failed to track feature usage');
    },
  });

  // Get feature limit details
  const getFeatureLimit = async (featureKey: string): Promise<TierLimitCheck | null> => {
    if (!user?.id) return null;
    
    try {
      return await tierService.checkUserLimit(user.id, featureKey);
    } catch (error) {
      console.error('Error getting feature limit:', error);
      return null;
    }
  };

  // Upgrade user to a new tier
  const upgradeTier = useMutation({
    mutationFn: async ({ tierId, grantCredits = true }: { tierId: string; grantCredits?: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');
      return tierService.assignUserToTier(user.id, tierId, grantCredits);
    },
    onSuccess: () => {
      // Invalidate user data to refresh tier info
      queryClient.invalidateQueries({ queryKey: ['user-tier'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast.success('Tier upgraded successfully!');
    },
    onError: (error) => {
      console.error('Error upgrading tier:', error);
      toast.error('Failed to upgrade tier');
    },
  });

  return {
    currentTier,
    tierLoading,
    checkFeatureAccess,
    trackFeatureUsage,
    getFeatureLimit,
    upgradeTier,
    isFreeTier: !currentTier || currentTier?.name === 'free',
    isProTier: currentTier?.name === 'pro',
    isEnterpriseTier: currentTier?.name === 'enterprise',
  };
};

// Hook for checking specific feature limits with UI feedback
export const useFeatureLimit = (featureKey: string) => {
  const { user } = useAuth();

  const { data: limitCheck, isLoading } = useQuery<TierLimitCheck | null>({
    queryKey: ['feature-limit', user?.id, featureKey],
    queryFn: async () => {
      if (!user?.id) return null;
      try {
        return await tierService.checkUserLimit(user.id, featureKey);
      } catch (error) {
        console.error('Error checking feature limit:', error);
        return null;
      }
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds to keep usage current
  });

  return {
    limitCheck,
    isLoading,
    canUseFeature: limitCheck?.allowed ?? false,
    usage: limitCheck?.current_usage ?? 0,
    limit: limitCheck?.limit_value ?? 0,
    remaining: limitCheck?.remaining ?? 0,
    isUnlimited: limitCheck?.limit_value === -1,
    resetPeriod: limitCheck?.reset_period,
    nextReset: limitCheck?.next_reset,
  };
};